import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// REDUX Import
import {
  EditUser,
  getSpecificUser,
} from "../../../redux/features/Settings/Management/Users";

// UTILS Imports
import { GlobalConfiguration } from "../../../utils/configurationProvider";
import Validation from "../../../utils/validation";

// COMPONENTS Imports
import PrimaryStepper from "../../../components/_utils/CommonSteppers/PrimaryStepper";
import SVGComponent from "../../../components/_utils/SVGComponent";
import ContactDetails from "../../../components/_utils/AccountCreationComponents/ContactDetails";
import LoginDetails from "../../../components/Settings/User/UserDetailsInput/LoginDetails";
import SmallPopup from "../../../components/_utils/CommonSmallPopup";
import CommonToast from "../../../components/_utils/CommonToast";
import ICONS from "../../../assets/Icons";

const MyProfileEditContactDetails = () => {
  // useParams
  const { id } = useParams();

  // useDispatch
  const dispatch = useDispatch();

  // useNavigate
  const navigate = useNavigate();

  // useState
  const [customErrors, setCustomErrors] = useState({});
  const [emailValidation, setEmailValidation] = useState(false);
  const [nextButtonEnabled, setNextButtonEnabled] = useState(false);
  const [openEditContactPop, setOpenEditContactPop] = useState(false);
  const [contactNumberValidation, setContactNumberValidation] = useState(false);

  const [payload, setPayload] = useState({
    contact_no: "",
    email_id: "",
    account_name: "",
    legalName: "",
    grade: "",
    user_id: "",
    alias: "",
    password: "",
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalPayload, setOriginalPayload] = useState(null);
  const [selectedRadioValue, setSelectedRadioValue] = useState("customname");

  // useSelector
  const { specificUserData } = useSelector((state) => state.users);
  const { editUserLoading } = useSelector((state) => state.users);

  // useEffect
  useEffect(() => {
    if (!payload || !originalPayload) {
      setHasUnsavedChanges(false);
      return;
    }

    const isDirty =
      payload.account_name.trim() !== originalPayload.account_name.trim() ||
      payload.contact_no.trim() !== originalPayload.contact_no.trim() ||
      payload.email_id.trim() !== originalPayload.email_id.trim() ||
      payload.grade.trim() !== originalPayload.grade.trim() ||
      payload.alias.trim() !== originalPayload.alias.trim();

    setHasUnsavedChanges(isDirty);
  }, [payload, originalPayload]);

  useEffect(() => {
    const contact = (payload.contact_no || "").trim();
    const email = (payload.email_id || "").trim();

    const contactValid =
      contact === "" ||
      (contact.length === GlobalConfiguration.owneraccountnormalLength &&
        Validation.allowedContactNum(contact));

    const emailValid = email === "" || Validation.emailRegex.test(email);

    setContactNumberValidation(contactValid);
    setEmailValidation(emailValid);
  }, [payload.contact_no, payload.email_id]);

  useEffect(() => {
    const fetchContactData = async () => {
      let response = await dispatch(getSpecificUser(id));
      if (response?.error?.message === "Request failed with status code 404") {
        navigate("*");
      }
    };
    fetchContactData();
  }, [dispatch, id, navigate]);

  useEffect(() => {
    if (!payload || !originalPayload) {
      setNextButtonEnabled(false);
      return;
    }

    const allFieldsFilled =
      payload.account_name.trim() !== "" &&
      payload.contact_no.trim() !== "" &&
      payload.email_id.trim() !== "" &&
      payload.grade.trim() !== "" &&
      payload.alias.trim() !== "";

    if (!allFieldsFilled) {
      setNextButtonEnabled(false);
      return;
    }

    const hasAnyChange =
      payload.account_name.trim() !== originalPayload.account_name.trim() ||
      payload.contact_no.trim() !== originalPayload.contact_no.trim() ||
      payload.email_id.trim() !== originalPayload.email_id.trim() ||
      payload.grade.trim() !== originalPayload.grade.trim() ||
      payload.alias.trim() !== originalPayload.alias.trim();

    if (!hasAnyChange) {
      setNextButtonEnabled(false);
      return;
    }

    const isNameValid =
      payload.account_name.trim().length >=
      GlobalConfiguration.accountminLength;
    const isContactValid =
      payload.contact_no.trim().length ===
        GlobalConfiguration.owneraccountnormalLength &&
      Validation.allowedContactNum(payload.contact_no.trim());
    const isEmailValid = Validation.emailRegex.test(
      payload.email_id.trim().toLowerCase(),
    );
    const isGradeValid = payload.grade.trim() !== "";
    const isAliasValid =
      payload.alias.trim().length > GlobalConfiguration.lengthCheckTwo &&
      allowedAlias(payload.alias.trim());
    const hasCustomErrors = Object.keys(customErrors).length > 0;

    setNextButtonEnabled(
      isNameValid &&
        isContactValid &&
        isEmailValid &&
        isGradeValid &&
        isAliasValid &&
        !hasCustomErrors,
    );
  }, [payload, originalPayload, customErrors]);

  useEffect(() => {
    if (specificUserData?.employees?.[GlobalConfiguration.lengthCheckZero]) {
      const user =
        specificUserData.employees[GlobalConfiguration.lengthCheckZero];

      const fullName = [user.first_name, user.last_name]
        .filter(Boolean)
        .join(" ");

      const panName = specificUserData?.pan_details?.full_name || "";

      const initialPayload = {
        account_name: fullName,
        fullName,
        legalName: panName,
        contact_no: user.mob_no ? String(user.mob_no) : "",
        email_id: user.email_id || "",
        grade: user.grade || "",
        user_id: user.identifier || user.user_name || "",
        alias: user.alias_name || "",
        password: "",
      };

      setPayload(initialPayload);
      setOriginalPayload(initialPayload);
      setSelectedRadioValue(
        fullName.toLowerCase() === panName.toLowerCase()
          ? "legalname"
          : "customname",
      );
    }
  }, [specificUserData]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (!allowedValue(name, value)) return;

    validateAndRecordError(name, value);

    setPayload((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (
      name === "account_name" &&
      value.trim().toLowerCase() === payload.legalName.trim().toLowerCase()
    ) {
      setSelectedRadioValue("legalname");
    }

    if (
      name === "account_name" &&
      value.trim().length > GlobalConfiguration.lengthCheckZero &&
      value.trim().toLowerCase() !== payload.legalName.trim().toLowerCase()
    ) {
      setSelectedRadioValue("customname");
    }
  };

  const allowedValue = (name, value) => {
    if (name === "contact_no") return Validation.allowedContactNum(value);
    if (name === "email_id") return Validation.allowedEmail(value);
    if (name === "alias") return allowedAlias(value);
    return true;
  };

  const validateAndRecordError = (name, value) => {
    let errors = { ...customErrors };
    if (name === "email_id")
      errors = Validation.validateEmail(name, value, errors);
    if (name === "contact_no")
      errors = Validation.validateContactNum(name, value, errors);
    if (name === "alias") {
      delete errors.alias;
      errors = validateAlias(name, value, errors);
    }

    setCustomErrors(errors);
  };

  const handleAttemptLeave = () => {
    if (hasUnsavedChanges) {
      setOpenEditContactPop(true);
    } else {
      navigate(GlobalConfiguration.navigate_back);
    }
  };

  const handleConfirmLeave = () => {
    setOpenEditContactPop(false);
    navigate(GlobalConfiguration.navigate_back);
  };

  const dataArray = [
    {
      previousText: "Cancel",
      nextText: "Update",
      stepperText: "Contact details",
      previousEnableBgColor: "var(--primary-nav-act)",
      nextEnableBgColor: "var(--positive-act)",
      nextDisableBgColor: "var(--text-disabled)",
      nextIsEnabled: nextButtonEnabled,
      nextHoverOption: "black",
      nextIsLoading: editUserLoading,
      nextLoaderNeeded: true,
    },
  ];
  const aliasRegex = /^[a-zA-Z0-9_-]+$/;

  const allowedAlias = (value) => {
    if (value === "") return true;
    return aliasRegex.test(value);
  };

  const validateAlias = (name, value, errors) => {
    if (!aliasRegex.test(value)) {
      errors[name] =
        "Only letters, numbers, underscore (_) and hyphen (-) are allowed";
    } else {
      delete errors[name];
    }
    return errors;
  };

  const getApiErrorMessage = (apiErrors) => {
    if (!apiErrors || typeof apiErrors !== "object") {
      return "Something went wrong while updating the user.";
    }

    return Object.values(apiErrors)
      .flatMap((messages) => (Array.isArray(messages) ? messages : [messages]))
      .filter(Boolean)
      .join(" ");
  };

  const handleUpdateContact = async () => {
    const nameParts = payload.account_name.trim().split(" ");
    const first_name = nameParts[GlobalConfiguration.lengthCheckZero] || "";
    const last_name = nameParts
      .slice(GlobalConfiguration.lengthCheckOne)
      .join(" ");

    try {
      const resultAction = await dispatch(
        EditUser({
          id,
          first_name,
          last_name,
          mob_no: payload.contact_no,
          email: payload.email_id?.toLowerCase(),
          grade: payload.grade,
          alias_name: payload.alias,
        }),
      );

      if (EditUser.fulfilled.match(resultAction)) {
        const updatedUser = {
          ...specificUserData,
          employees: [
            {
              ...specificUserData.employees[
                GlobalConfiguration.lengthCheckZero
              ],
              first_name,
              last_name,
              mob_no: payload.contact_no,
              email_id: payload.email_id?.toLowerCase(),
              grade: payload.grade,
              alias_name: payload.alias,
            },
          ],
        };

        navigate(`/settings/my_profile/contact_details_success/${id}`, {
          state: { user: updatedUser },
        });
        return;
      }

      if (EditUser.rejected.match(resultAction)) {
        const apiErrors = resultAction.payload?.errors;
        const errorMessage = getApiErrorMessage(apiErrors);

        if (apiErrors?.alias_name?.[GlobalConfiguration.lengthCheckZero]) {
          setCustomErrors((prev) => ({
            ...prev,
            alias: apiErrors.alias_name[GlobalConfiguration.lengthCheckZero],
          }));
        }

        CommonToast({
          text: errorMessage,
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      }
    } catch (err) {
      console.error("Update failed:", err);
      CommonToast({
        text: "Something went wrong while updating the user.",
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
    }
  };

  return (
    <>
      <div className=" bg-primary-bg flex overflow-y-hidden flex-col h-full-height">
        <div className="hidden Qsmd:block min-h-navbar-breadcrumb-height Qlg:bg-primary-bg bg-primary-nav"></div>
        <div
          className="max-w-tab-width-normal  w-full flex flex-col justify-start items-center mx-auto my-margin-base"
          style={{
            overflow: !nextButtonEnabled && openEditContactPop ? "hidden" : "",
          }}
        >
          {openEditContactPop && (
            <div className="fixed top-top-base left-left-base w-width-full h-height-full flex items-center bg-PopUp-BackDrop justify-center z-z-index-modal bg-opacity-dark-opacity">
              <SmallPopup
                headerTitle={"Open in full view?"}
                headerFontWeight="semibold"
                headerFontColor="text-primary"
                textSize="font-md"
                subtext="Opening in full view will leave this page and exit the current flow."
                btnOneText="Stay"
                btnOneBtnType="button"
                btnOneEnabledBgColor="transparent"
                btnOneHandleFunction={() => setOpenEditContactPop(false)}
                btnOneOutLineColor="var(--text-primary)"
                btnOneEnabledTextColor="var(--text-primary)"
                btnOneIsDisabled={false}
                btnOneHoverOption="white"
                btnTwoText="Leave"
                btnTwoBtnType="button"
                btnTwoHandleFunction={handleConfirmLeave}
                btnTwoEnabledBgColor="var(--negative-act)"
                btnTwoEnabledTextColor="var(--text-primary)"
                btnTwoIsDisabled={false}
                btnTwoHoverOption="black"
              />
            </div>
          )}

          <div className="h-auto flex justify-center items-end bottom-bottom-base  Qsmd:flex fixed Qsmd:top-top-base left-left-base w-width-full z-z-index-header Qsmd:relative min-h-stepper-height">
            <PrimaryStepper
              activeStep={GlobalConfiguration.lengthCheckZero}
              handleForwardAction={handleUpdateContact}
              handleBackwardAction={handleAttemptLeave}
              dataArray={dataArray}
            />
          </div>
          <div className="mt-margin-base fixed top-top-base left-left-base w-full z-z-index-header">
            <div className="h-navbar-height bg-primary-nav fixed top-top-base w-width-full flex items-center z-z-index-header">
              <div
                className="w-icon-sm h-icon-sm m-margin-md cursor-pointer"
                onClick={handleAttemptLeave}
              >
                <SVGComponent
                  url={ICONS?.ARROW_LEFT_WHITE}
                  height="var(--icon-sm)"
                  width="var(--icon-sm)"
                  color="var(--text-primary)"
                  alt="arrow left"
                />
              </div>
              <div className="py-padding-sm">
                <p className="text-text-primary text-font-md">Users</p>
                <p className="text-text-secondary text-font-xs">
                  Contact details
                </p>
              </div>
            </div>
          </div>
          <div className="h-navbar-height block Qsmd:hidden"></div>
        </div>

        <div className="flex flex-row h-height-full max-w-tab-width-normal mx-auto w-full flex-1 overflow-auto scrollbar-none">
          <div className=" w-full Qsmd:h-full h-[var(--width-almost-full)] ">
            <div className="w-width-full h-full mt-margin-base overflow-auto scrollbar-none px-padding-md Qsmd:pt-padding-sm lg:px-padding-base Qmd:gap-gap-xl flex flex-col Qmd:flex-row pb-padding-md">
              {/* <div
                className={`flex flex-col relative w-full rounded-border-radius-md mb-margin-md Qmd:mt-margin-xl pt-padding-lg`}
              >
                <AccountName
                  handleChange={handleChange}
                  accountName={payload.account_name}
                  legalName={payload.legalName}
                  userName={userName}
                  selectedRadioValue={selectedRadioValue}
                  handleDivBtn={handleDivBtn}
                  handleRadioBtn={handleRadioBtn}
                  accountNameValidation={accountNameValidation}
                  placeHolder="Account name"
                />
                <UserGrade
                  userPayload={payload}
                  setUserPayload={setPayload}
                  gradeOption={gradeOption}
                />
              </div> */}
              <div
                className={`flex flex-col relative rounded-border-radius-md w-full Qsmd:w-width-md mb-margin-md pt-padding-md`}
              >
                <ContactDetails
                  handleChange={handleChange}
                  contactNumber={payload.contact_no}
                  emailId={payload.email_id}
                  contactNumberValidation={contactNumberValidation}
                  emailValidation={emailValidation}
                />
                <LoginDetails
                  userPayload={payload}
                  handleChange={handleChange}
                  showUserId={false}
                  type="my_profile"
                  showPassword={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MyProfileEditContactDetails;
